import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCheck,
  faCircleCheck,
  faClock,
  faTimes,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import ApiServices from '../Services/ApiServices';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import { toastHelper } from '../../Helpers/toastHelper';
import { useLanguage } from '../../contexts/LanguageContext';

const resolveMarkAsReadEndpoint = (markAsReadAPI, notificationId) => {
  const id = String(notificationId);

  if (typeof markAsReadAPI === 'function') {
    return markAsReadAPI(notificationId);
  }

  if (!markAsReadAPI || typeof markAsReadAPI !== 'string') {
    return `Notification/markAsRead/${id}`;
  }

  if (markAsReadAPI.includes(':id')) {
    return markAsReadAPI.replace(':id', id);
  }

  return markAsReadAPI.endsWith('/') ? `${markAsReadAPI}${id}` : `${markAsReadAPI}/${id}`;
};

const NotificationPanel = ({
  notifications = [],
  onClose,
  panelStyle,
  markAsReadAPI = 'Notification/markAsRead',
  getNotificationsAPI = 'Notification/getAll',
  pageSize = 10,
  onNotificationRead,
  onMarkAllRead,
  title = 'الإشعارات',
}) => {
  const { t } = useLanguage();
  const [items, setItems] = useState(Array.isArray(notifications) ? notifications : []);
  const [submittingId, setSubmittingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    setItems(Array.isArray(notifications) ? notifications : []);
    setCurrentPage(1);
    setHasMore(true);
  }, [notifications]);

  const extractPaginationPayload = (response) => {
    let payload = response;
    const dataRoot = payload?.data || payload;
    const fetchedItems = Array.isArray(dataRoot?.items)
      ? dataRoot.items
      : Array.isArray(dataRoot)
        ? dataRoot
        : [];

    const totalPages = Number(dataRoot?.total_pages || payload?.total_pages || 0);

    return {
      fetchedItems,
      totalPages,
    };
  };

  const loadMoreNotifications = async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPage + 1;

    try {
      setLoadingMore(true);

      const response = await ApiServices.get(getNotificationsAPI, {
        PageSize: pageSize,
        PageNumber: nextPage,
        IsActive: true,
      });

      const { fetchedItems, totalPages } = extractPaginationPayload(response);

      if (!Array.isArray(fetchedItems) || fetchedItems.length === 0) {
        setHasMore(false);
        return;
      }

      setItems((prev) => {
        const seen = new Set(prev.map((item) => item.id));
        const uniqueNew = fetchedItems.filter((item) => !seen.has(item.id));
        return [...prev, ...uniqueNew];
      });

      setCurrentPage(nextPage);

      if ((totalPages > 0 && nextPage >= totalPages) || fetchedItems.length < pageSize) {
        setHasMore(false);
      }
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleListScroll = (event) => {
    const node = event.currentTarget;
    const threshold = 100;
    const nearBottom = node.scrollHeight - node.scrollTop - node.clientHeight < threshold;

    if (nearBottom) {
      loadMoreNotifications();
    }
  };

  const unreadCount = useMemo(
    () => items.filter((item) => !item.is_read).length,
    [items]
  );

  const markAsRead = async (item) => {
    if (!item || item.is_read || submittingId === item.id) return;

    try {
      setSubmittingId(item.id);
      const endpoint = resolveMarkAsReadEndpoint(markAsReadAPI, item.id);
      await ApiServices.post(endpoint);

      setItems((prev) =>
        prev.map((notification) =>
          notification.id === item.id
            ? { ...notification, is_read: true }
            : notification
        )
      );

      if (onNotificationRead) {
        onNotificationRead(item.id);
      }
    } catch (error) {
      toastHelper.error(t('notificationUpdateFailed'));
    } finally {
      setSubmittingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    if (!onMarkAllRead) return;

    try {
      await onMarkAllRead();
      setItems((prev) => prev.map((item) => ({ ...item, is_read: true })));
    } catch (error) {
      toastHelper.error(t('markAllReadFailed'));
    }
  };

  return (
    <motion.div
      className="np-root"
      initial={{ opacity: 0, y: 10, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.2, ease: [0.21, 1, 0.36, 1] }}
      style={panelStyle}
    >
      <div className="np-head">
        <div className="np-title-wrap">
          <h3>{title}</h3>
          {unreadCount > 0 && <span className="np-badge">{unreadCount}</span>}
        </div>

        <div className="np-actions">
          <button type="button" className="np-mark-all" onClick={handleMarkAllRead}>
            {t('markAllAsRead')}
          </button>
          <button
            type="button"
            className="np-close"
            onClick={onClose}
            aria-label={t('closeNotifications')}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>

      <div className="np-list" onScroll={handleListScroll}>
        {items.length === 0 ? (
          <div className="np-empty">
            <div className="np-empty-icon">
              <FontAwesomeIcon icon={faBell} />
            </div>
            <p>{t('noNotifications')}</p>
          </div>
        ) : (
          <>
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`np-item ${item.is_read ? '' : 'is-unread'}`}
                onClick={() => markAsRead(item)}
              >
                <div className="np-avatar">
                  {item.from_user_image ? (
                    <img
                      src={getFullFileUrl(item.from_user_image)}
                      alt={item.from_user || t('defaultUser')}
                      onError={(event) => {
                        event.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <FontAwesomeIcon icon={faUser} />
                  )}
                </div>

                <div className="np-body">
                  <div className="np-top">
                    <span className="np-user">{item.from_user || t('defaultUser')}</span>
                    <span className="np-time">
                      <FontAwesomeIcon icon={faClock} />
                      {item.created_at_date}
                    </span>
                  </div>

                  <p>{item.body || item.notification_type || t('newNotification')}</p>

                  <div className="np-foot">
                    <span className="np-type">{item.notification_type || t('notification')}</span>
                    <span className={`np-read ${item.is_read ? 'done' : ''}`}>
                      <FontAwesomeIcon icon={item.is_read ? faCircleCheck : faCheck} />
                      {submittingId === item.id ? t('updating') : item.is_read ? t('readStatus') : t('unreadStatus')}
                    </span>
                  </div>
                </div>
              </button>
            ))}

            {loadingMore && <div className="np-load-more">{t('loadingMoreNotifications')}</div>}
            {!hasMore && items.length > 0 && <div className="np-load-more done">{t('noMoreNotifications')}</div>}
          </>
        )}
      </div>

      <style jsx>{`
        .np-root {
          position: absolute;
          direction: rtl;
          text-align: right;
          width: 360px;
          max-height: min(72vh, 560px);
          background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
          border-radius: 18px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 24px 50px rgba(15, 23, 42, 0.16);
          overflow: hidden;
          z-index: 1200;
          font-family: "Zain", sans-serif;
          display: flex;
          flex-direction: column;
        }

        .np-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
          padding: 14px;
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
          color: #fff;
        }

        .np-title-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          flex: 1 1 auto;
        }

        .np-title-wrap h3 {
          margin: 0;
          font-size: 1.12rem;
          font-weight: 900;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .np-badge {
          min-width: 22px;
          height: 22px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.22);
          border: 1px solid rgba(255, 255, 255, 0.36);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 800;
          padding: 0 6px;
        }

        .np-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 0 1 auto;
          min-width: 0;
        }

        .np-mark-all,
        .np-close {
          border: none;
          color: #fff;
          background: rgba(255, 255, 255, 0.22);
          border-radius: 10px;
          cursor: pointer;
          font-family: inherit;
          font-weight: 800;
        }

        .np-mark-all {
          font-size: 0.78rem;
          padding: 6px 10px;
          white-space: nowrap;
          max-width: 170px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .np-close {
          width: 30px;
          height: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .np-list {
          padding: 10px;
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          display: grid;
          gap: 8px;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }

        .np-list::-webkit-scrollbar {
          width: 6px;
        }

        .np-list::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 6px;
        }

        .np-item {
          width: 100%;
          border: 1px solid #e2e8f0;
          background: #fff;
          border-radius: 12px;
          padding: 10px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          cursor: pointer;
          text-align: right;
          transition: all 0.2s ease;
          font-family: inherit;
        }

        .np-item:hover {
          border-color: #c7d2fe;
          box-shadow: 0 8px 18px rgba(99, 102, 241, 0.12);
          transform: translateY(-1px);
        }

        .np-item.is-unread {
          border-color: #a5b4fc;
          background: #f5f7ff;
        }

        .np-avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          overflow: hidden;
          background: #eef2ff;
          color: #4f46e5;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 1px solid #dbeafe;
        }

        .np-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .np-body {
          min-width: 0;
          flex: 1;
        }

        .np-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 4px;
        }

        .np-user {
          color: #1e293b;
          font-weight: 900;
          font-size: 0.92rem;
        }

        .np-time {
          color: #64748b;
          font-size: 0.72rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }

        .np-body p {
          margin: 0;
          color: #334155;
          line-height: 1.45;
          font-size: 0.86rem;
          overflow-wrap: anywhere;
        }

        .np-foot {
          margin-top: 8px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .np-type {
          background: #eef2ff;
          color: #4f46e5;
          border-radius: 999px;
          padding: 2px 8px;
          font-size: 0.68rem;
          font-weight: 800;
        }

        .np-read {
          color: #64748b;
          font-size: 0.72rem;
          font-weight: 800;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .np-read.done {
          color: #16a34a;
        }

        .np-empty {
          min-height: 200px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          gap: 8px;
        }

        .np-empty-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eef2ff;
          color: #6366f1;
          font-size: 1.4rem;
        }

        .np-empty p {
          margin: 0;
          font-weight: 800;
        }

        .np-load-more {
          text-align: center;
          color: #64748b;
          font-weight: 800;
          font-size: 0.8rem;
          padding: 8px;
        }

        .np-load-more.done {
          color: #94a3b8;
        }

        @media (max-width: 768px) {
          .np-root {
            position: fixed;
            top: 12px !important;
            right: 10px !important;
            left: 10px !important;
            width: auto !important;
            max-height: calc(100vh - 24px);
            border-radius: 16px;
          }

          .np-head {
            display: grid;
            grid-template-columns: 1fr;
            align-items: stretch;
            gap: 10px;
            padding: 12px;
          }

          .np-title-wrap {
            justify-content: space-between;
          }

          .np-actions {
            width: 100%;
            justify-content: flex-start;
            gap: 6px;
          }

          .np-mark-all {
            font-size: 0.72rem;
            padding: 5px 8px;
            flex: 1;
            max-width: none;
            text-align: center;
          }

          .np-close {
            width: 28px;
            height: 28px;
          }

          .np-list {
            flex: 1;
            min-height: 0;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default NotificationPanel;
